import React, { useState } from "react"
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "components/ui/table"
import { Badge } from "components/ui/badge"

import { useIdentities, useDeleteIdentity, useRefreshIdentity, useAddIdentity } from "core/features/identity/hooks"
import { Check, RefreshCw, Trash2, Plus, ShieldCheck, Mail, Building2, IdCard } from "lucide-react"
import { toast } from "components/ui/toast"
import AddIdentity from "./add-identity/AddIdentity"
import Button from "components/ui/button"
import { IdentityTableSkeleton, IdentityMobileCardSkeleton } from "skeleton/identity/IdentitySkeleton"
import { SkeletonSwitch } from "skeleton"
import ConfirmationDialog from "components/ui/confirmationDialog"
import { PageContainer, PageContentLayout, PageCardGrid } from "../ui/page-container"
import { EmptyState } from "components/ui/empty-state"
import { ErrorState } from "components/ui/error-state"
import PageHeader, { PrimaryButton } from "components/ui/PageHeader"

export default function IdentityComponent() {
    const [showModal, setShowModal] = React.useState(false)
    const {
        data: identities,
        isFetching: identityLoading,
        isError: identityError,
        error: identityErrorObj,
        refetch: refetchIdentities,
    } = useIdentities()
    const deleteMut = useDeleteIdentity()
    const refreshMut = useRefreshIdentity()
    const resendMut = useAddIdentity({ skipInvalidate: true })
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState<boolean>(false)
    const [selectedIdentity, setSelectedIdentity] = useState<string | null>(null)

    const onRefresh = async (identityEmail: string) => {
        try {
            toast.info("Refreshing identity...");
            await refreshMut.mutateAsync(identityEmail);
            toast.dismiss();
            toast.success("Identity refreshed.");
        }
        catch (error) {
            console.error("Error refreshing identity:", error);
            toast.error("Failed to refresh identity.");
        }
    }

    const onDelete = async (identityEmail: string) => {
        try {
            toast.info("Deleting identity...");
            await deleteMut.mutateAsync(identityEmail);
            toast.dismiss();
            toast.success("Identity deleted.");
        }
        catch (error) {
            console.error("Error deleting identity:", error);
            toast.error("Failed to delete identity.");
        }
    }

    const onResend = async (identityEmail: string, friendlyName: string) => {
        try {
            toast.info("Resending verification email...");
            await resendMut.mutateAsync({ email: identityEmail, friendlyName });
            toast.dismiss();
            toast.success("Verification email resent. Please check your inbox (and spam).");
        }
        catch (error) {
            console.error("Error resending verification email:", error);
            toast.error("Failed to resend verification email.");
        }
    }

    return (
        <PageContainer>
            <PageHeader
                icon={<ShieldCheck className="w-5 h-5" />}
                iconBg="bg-emerald-50 dark:bg-emerald-900/20"
                iconColor="text-emerald-600 dark:text-emerald-400"
                accentColor="bg-gradient-to-r from-emerald-400 to-teal-500"
                title="Identity Management"
                subtitle="Verify and manage your sender email addresses"
                actions={
                    <PrimaryButton
                        icon={<Plus className="w-4 h-4" />}
                        onClick={() => setShowModal(true)}
                    >
                        Verify Email
                    </PrimaryButton>
                }
            />

            {/*
              * Identity had no empty state: with nothing verified the table
              * rendered its headers over blank space, and a failed request
              * looked identical. Both now say what happened and what to do.
              */}
            {!identityLoading && (identityError || !identities?.items?.length) ? (
                identityError ? (
                    <ErrorState
                        title="Couldn't load your sender identities"
                        description="The request didn't complete. Your verified senders are unaffected — this is only the view."
                        error={identityErrorObj}
                        onRetry={() => refetchIdentities()}
                    />
                ) : (
                    <EmptyState
                        icon={IdCard}
                        title="No sender identities yet"
                        description="Verify an email address before you can send from it. Verification takes a minute and only has to be done once per address."
                        primaryAction={{
                            label: "Verify email",
                            onClick: () => setShowModal(true),
                            icon: <Plus className="w-4 h-4" />,
                        }}
                    />
                )
            ) : (
            <PageContentLayout
                mobileContent={
                    <SkeletonSwitch isLoading={identityLoading} skeleton={<IdentityMobileCardSkeleton />}>
                        <PageCardGrid>
                            {identities?.items && identities.items.map((row) => {
                                const isVerified = row.status === "Success";
                                const isRefreshing = refreshMut.isPending && refreshMut.variables === row.email;
                                const isResending = resendMut.isPending && resendMut.variables?.email === row.email;

                                return (
                                    <div
                                        key={row.id}
                                        className="relative overflow-hidden bg-surface dark:bg-dark-800 rounded-2xl border border-line-subtle dark:border-dark-700/80 shadow-sm hover:shadow-md transition-all duration-200"
                                    >
                                        {/* Left vertical status strip */}
                                        <div className={`absolute left-0 top-0 bottom-0 w-[4px] rounded-l-2xl bg-gradient-to-b ${isVerified ? "from-emerald-400 to-teal-500" : "from-amber-400 to-orange-500"}`} />

                                        {/* Card body */}
                                        <div className="pl-5 pr-4 pt-4 pb-3">
                                            <div className="flex items-start justify-between gap-3">
                                                {/* Left: email + business name */}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-1.5 mb-1">
                                                        <Mail className="w-3.5 h-3.5 text-content-subtle dark:text-gray-500 flex-shrink-0" />
                                                        <p className="text-ui font-bold text-content dark:text-white truncate leading-snug">
                                                            {row.email}
                                                        </p>
                                                    </div>
                                                    {row.friendlyName && (
                                                        <div className="flex items-center gap-1.5">
                                                            <Building2 className="w-3 h-3 text-gray-300 dark:text-gray-600 flex-shrink-0" />
                                                            <p className="text-meta font-medium text-content-subtle dark:text-gray-500 truncate">
                                                                {row.friendlyName}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Right: verification status badge */}
                                                <span className={`flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase border ${isVerified
                                                    ? "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800/50"
                                                    : "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800/50"}`}>
                                                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isVerified ? "bg-emerald-500" : "bg-red-500"}`} />
                                                    {row.status}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Card footer */}
                                        <div className="pl-5 pr-4 pb-3 flex items-center justify-between">
                                            <div>
                                                {!isVerified ? (
                                                    <button
                                                        disabled={isResending}
                                                        onClick={() => onResend(row.email, row.friendlyName)}
                                                        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-[12px] font-semibold text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 shadow-sm transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                                                    >
                                                        <RefreshCw className={`w-3.5 h-3.5 ${isResending ? "animate-spin" : ""}`} />
                                                        {isResending ? "Sending…" : "Verify"}
                                                    </button>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[12px] font-semibold text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20">
                                                        <Check className="w-3.5 h-3.5" />
                                                        Verified
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-0.5">
                                                <button
                                                    onClick={() => onRefresh(row.email)}
                                                    disabled={isRefreshing}
                                                    title="Refresh Identity"
                                                    className="p-2 rounded-xl text-blue-500 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors disabled:opacity-50"
                                                >
                                                    <RefreshCw className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`} />
                                                </button>
                                                <button
                                                    onClick={() => { setDeleteConfirmationOpen(true); setSelectedIdentity(row.email); }}
                                                    title="Delete Identity"
                                                    className="p-2 rounded-xl text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </PageCardGrid>
                    </SkeletonSwitch>
                }
                desktopContent={
                    <div className="shadow-sm">
                        <SkeletonSwitch isLoading={identityLoading} skeleton={<IdentityTableSkeleton />}>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Action</TableHead>
                                        <TableHead>Email Address</TableHead>
                                        <TableHead>Verification Status</TableHead>
                                        <TableHead>Business Name</TableHead>
                                        <TableHead>Status</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {identities?.items && identities?.items.map((row) => (
                                        <TableRow key={row.id}>
                                            <TableCell className="flex items-center gap-3">
                                                <button
                                                    onClick={() => onRefresh(row.email)}
                                                    className="p-1.5 rounded-full hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors"
                                                    title="Refresh Identity"
                                                    disabled={refreshMut.isPending && refreshMut.variables === row.email}
                                                >
                                                    <RefreshCw className={`w-4 h-4 text-blue-600 dark:text-blue-300 ${refreshMut.isPending && refreshMut.variables === row.email ? "animate-spin" : ""}`} />
                                                </button>
                                                <button
                                                    onClick={() => { setDeleteConfirmationOpen(true); setSelectedIdentity(row.email); }}
                                                    className="p-1.5 rounded-full hover:bg-red-100 dark:hover:bg-red-900 transition-colors"
                                                    title="Delete Identity"
                                                >
                                                    <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                                                </button>
                                            </TableCell>
                                            <TableCell>{row.email}</TableCell>
                                            <TableCell>
                                                <Badge variant={row.status === "Success" ? "success" : "error"}>{row.status}</Badge>
                                            </TableCell>
                                            <TableCell>{row.friendlyName}</TableCell>
                                            <TableCell>
                                                {row.status !== "Success" ? (
                                                    <Button
                                                        size="sm"
                                                        className="text-xs px-3 py-1 h-auto font-medium hover-lift transition-all inline-flex items-center gap-1.5"
                                                        disabled={resendMut.isPending && resendMut.variables?.email === row.email}
                                                        onClick={() => onResend(row.email, row.friendlyName)}
                                                    >
                                                        <RefreshCw className="w-3.5 h-3.5" />
                                                        <span>{resendMut.isPending && resendMut.variables?.email === row.email ? "Sending..." : "Verify"}</span>
                                                    </Button>
                                                ) : (
                                                    <Badge variant="success" className="inline-flex items-center gap-1 px-2 py-0.5 text-xs">
                                                        <Check className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
                                                        <span>Verified</span>
                                                    </Badge>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </SkeletonSwitch>
                    </div>
                }
            />
            )}

            <ConfirmationDialog
                open={deleteConfirmationOpen}
                title="Delete this Identity?"
                description="This action cannot be undone."
                confirmLabel="Delete"
                cancelLabel="Cancel"
                variant="destructive"
                onConfirm={async () => {
                    onDelete(selectedIdentity)
                    setDeleteConfirmationOpen(false);
                    setSelectedIdentity(null);
                }}
                onCancel={() => setDeleteConfirmationOpen(false)}
            />

            <AddIdentity
                open={showModal}
                onClose={() => setShowModal(false)}
            />
        </PageContainer>
    )
}
